import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  IconButton,
  TextField,
  Button,
  Typography,
  Grid,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import Images from '../../../Utils/images';
import '../../../styles/edit-shift-modal.scss';
import moment from 'moment-timezone';
import TimePicker from '../../../Components/TimePicker';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DateInput from '../../../Components/DateInput';
import dayjs, { Dayjs } from 'dayjs';
import { useForm, Controller, useFieldArray, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { updateShiftPreview } from '../../../redux/orderSlice';

import { notify } from '../../../Utils/toastify';
import { extendServiceDateRangeSchema } from '../../../Utils/yupvalidations';
import LocationUpdateModalShift, {
  LocationOption,
} from './LocationUpdateModalShift';

import WarningDialog from '../../../Components/WarningDialog';
import { ROUTES, TIME_PICKER_MINUTES_STEP } from '../../../helpers/contants';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  alignShiftEndToStart,
  getCoverage,
  getTimeDisplay,
} from '../../../Utils/helperFns';
import TimePickerControlled from '../../../Components/TimePickerControlled';
import ToolTipComponent from '../../../Components/ToolTipComponent';
import { Worksheet } from '../../MyOrders/Components/ShiftDetails';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../helpers/analyticsEvents';

const TimePickerControlledAny = TimePickerControlled as any;

/** ---------------- Types ---------------- */

interface Shift {
  _id: string;
  actualStartTime: string; // ISO
  actualEndTime: string; // ISO
  duration: number;
  unit?: number; // original units
  shiftCoverage?: 'Day' | 'Night';
  vendorOrderId?: string;
  orderId?: string;
  worksheetId?: string;
  deliveryAddress: {
    locationName: string;
    addressType?: string;
    address?: string;
    apartment?: string | null;
    buildingName?: string | null;
    productionType?: string;
    productionId?: string;
    productionName?: string;
    setId?: string;
    setName?: string;
    locationId?: string;
    productionScheduleType?: string;
    setScheduleType?: string;
  };
  status?: string;
  approveBy?: string;
  updatedBy?: any;
}

interface CartDetails {
  startTime?: string; // ISO
  endTime?: string; // ISO
  totalCost?: number;
  totalHours?: number;
}

interface EditShiftModalProps {
  open: boolean;
  onClose: () => void;
  setSelectedWorkSheet: (worksheet: Worksheet) => void;
  selectedWorksheet?: Worksheet;
  selectedShifts?: Shift[];
  cartDetails?: CartDetails;
  buildApiQuery: (page: number) => any;
  selectedWorksheetShifts?: {
    [worksheetId: string]: {
      shifts: Shift[];
      worksheet: Worksheet;
    };
  };
  setSelectedDate: (date: any) => void;
  displayData: any;
}

/** ------------- Helpers ------------- */

const determineShiftType = (shifts: Shift[]): 'Day' | 'Night' => {
  if (!shifts || shifts.length === 0) return 'Day';
  const hasDayShift = shifts.some(shift => {
    const startTime = new Date(shift.actualStartTime);
    const minutes = startTime.getHours() * 60 + startTime.getMinutes();
    return minutes < 15 * 60; // before 3 PM
  });
  return hasDayShift ? 'Day' : 'Night';
};

/** ------------- Form Types ------------- */

interface ExtendShiftField {
  startDate?: Dayjs;
  endDate?: Dayjs;
}

interface WorksheetForm {
  worksheetId: string;
  meta: Worksheet; // keep entire worksheet for payload building
  startTime?: Dayjs;
  endTime?: Dayjs;
  locationOverride?: LocationOption;
  // shifts array (we track updatedUnits per shift)
  shifts: Array<{
    shiftId: string;
    original: Shift;
    updatedUnits: number;
    totalUnits: number;
  }>;
  // extend shifts per worksheet
  extendShifts: ExtendShiftField[];
  notes?: string;
}

interface WorksheetEditForm {
  worksheetId: string;
  worksheet: any;
  shifts: any[];
  originalCoverage: string;
  originalShifts: any[];
  startTime?: any;
  endTime?: any;
  notes?: string;
  locationOverride?: LocationOption;
  extendShifts?: Array<{
    startDate?: any;
    endDate?: any;
  }>;
  bulkUpdate: number;
  shiftCoverage?: 'Day' | 'Night';
}

interface EditFormValues {
  worksheets: WorksheetEditForm[];
}

const schema = yup
  .object({
    worksheets: yup
      .array()
      .of(
        yup.object({
          worksheetId: yup.string().required(),
          worksheet: yup.object(),
          shifts: yup.array().of(yup.object()),
          originalCoverage: yup.string().required(),
          originalShifts: yup.array().of(yup.object()),
          bulkUpdate: yup.number().min(1).required(),
          shiftCoverage: yup.mixed().nullable(),
          startTime: yup
            .mixed()
            .test('is-dayjs', 'Invalid start time', value => {
              return value === null || dayjs.isDayjs(value);
            }),
          endTime: yup.mixed().test('is-dayjs', 'Invalid end time', value => {
            return value === null || dayjs.isDayjs(value);
          }),
          notes: yup
            .string()
            .max(1000, 'Max 1000 characters')
            .test(
              'not-only-spaces',
              'Notes cannot contain only spaces',
              value => !value || value.trim().length > 0,
            )
            .optional(),
          locationOverride: yup.object().optional(),
          extendShifts: yup.array().of(extendServiceDateRangeSchema),
        }),
      )
      .required(),
  })
  .required();

const shiftCoverage = {
  Day: 'Day',
  Night: 'Night',
  All: 'All',
};

/** ------------- Component ------------- */

const EditShiftModal: React.FC<EditShiftModalProps> = ({
  open,
  onClose,
  setSelectedWorkSheet,
  selectedWorksheet,
  selectedShifts = [],
  cartDetails,
  buildApiQuery,
  selectedWorksheetShifts = {},
  setSelectedDate,
  displayData,
}) => {
  /** Stepper per worksheet */
  const [activeStep, setActiveStep] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { product } = location.state;
  /** Single location modal (driven by current step) */
  const [openLocationUpdateModal, setOpenLocationUpdateModal] = useState(false);
  /** Notes state for each worksheet */
  const [notesState, setNotesState] = useState<{ [key: number]: string }>({});
  const [warningOpen, setWarningOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState({
    starttextField: false,
    startpickerOpen: false,
    endPickerOpen: false,
    endTextField: false,
    isStartAccepted: false,
    isEndAccepted: false,
  });
  const [shiftType, setShiftType] = useState('');
  const { product: productDetails } = useLocation()?.state;
  /** Build default form values from selectedWorksheetShifts on open */
  const defaultValues: EditFormValues = useMemo(() => {
    const ids = Object.keys(selectedWorksheetShifts || {});
    const worksheets = ids.map(id => {
      const w = selectedWorksheetShifts[id]?.worksheet as Worksheet;
      const shifts =
        shiftType == 'All' || shiftType === ''
          ? ((selectedWorksheetShifts[id]?.shifts || []) as Shift[])
          : ((selectedWorksheetShifts[id]?.shifts || []).filter(
              val => getCoverage(val.actualStartTime) === shiftType,
            ) as Shift[]);
      const first = shifts[0];
      const originalCoverageCandidate =
        new Set(
          (selectedWorksheetShifts[id]?.shifts || []).map(s =>
            getCoverage(s.actualStartTime),
          ),
        ).size > 1
          ? 'All'
          : selectedWorksheetShifts[id]?.shifts?.[0]?.actualStartTime
          ? getCoverage(
              selectedWorksheetShifts[id]?.shifts?.[0]?.actualStartTime,
            )
          : 'All';
      const originalCoverage = originalCoverageCandidate || 'All';
      const grouped = shifts.reduce((acc, s) => {
        const key = `${s.actualStartTime || ''}_${s.actualEndTime || ''}_${
          s.deliveryAddress.locationId || ''
        }_${s.deliveryAddress.setId}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(s);
        return acc;
      }, {} as Record<string, Shift[]>);

      const mergedShifts = Object.values(grouped).map(group => {
        return {
          shiftId: group[0]?._id, // or keep array if needed
          original: group[0],
          updatedUnits: group[0]?.unit, // bundle units
          totalUnits: group.reduce((sum, s) => sum + (s.unit || 1), 0),
          initialUnits: group.reduce((sum, s) => sum + (s.unit || 1), 0),
          isMerged: group.length > 1,
          shiftIds: group.map(s => s._id),
        };
      });
      return {
        worksheetId: id,
        shiftCoverage: first?.shiftCoverage,
        worksheet: w,
        shifts: mergedShifts,
        originalShifts: shifts.map(val => {
          return {
            shiftId: val._id,
            original: val,
            updatedUnits: val.unit,
          };
        }),
        originalCoverage: originalCoverage ?? 'Day',
        startTime: first?.actualStartTime
          ? dayjs(first.actualStartTime)
          : undefined,
        endTime: first?.actualEndTime ? dayjs(first.actualEndTime) : undefined,
        extendShifts: [],
        notes: '',
        bulkUpdate: 1,
      };
    });
    return { worksheets };
  }, [selectedWorksheetShifts, shiftType]);

  const form = useForm<EditFormValues>({
    resolver: yupResolver(schema) as unknown as Resolver<EditFormValues>,
    mode: 'onChange',
    defaultValues,
    values: open ? defaultValues : undefined, // rehydrate when opened with fresh data
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    getValues,
    reset,
    formState: { errors, isValid },
  } = form;

  /** Top-level worksheets field array */
  const {
    fields: worksheetFields,
    append: appendWorksheet,
    remove: removeWorksheet,
    update: updateWorksheet,
  } = useFieldArray({
    control,
    name: 'worksheets',
  });

  // Remove nested field arrays to fix React error

  /** Handle modal open/close reinit */
  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setActiveStep(0);
    }
  }, [open, shiftType]); // eslint-disable-line

  /** Navigation */
  const handleNext = () => {
    saveCurrentStepData();
    if (activeStep < worksheetFields.length - 1) setActiveStep(s => s + 1);
  };
  const handleBack = () => {
    saveCurrentStepData();
    if (activeStep > 0) setActiveStep(s => s - 1);
  };
  const handleStepClick = (i: number) => {
    saveCurrentStepData();
    setActiveStep(i);
  };

  /** Shortcuts to current worksheet */
  const current = worksheetFields[activeStep];
  const currentPath = `worksheets.${activeStep}` as const;
  const isAfterCurrentTime = useCallback(
    time => {
      const currentTime = moment();
      const isAfterCurrentTime = moment(time).isAfter(currentTime);
      return isAfterCurrentTime;
    },
    [current],
  );

  const isFixedCost = !!current?.worksheet?.rentPricing?.isFlexibleShiftHr;
  const hasAnyFutureShift = (watch(`${currentPath}.shifts`) || []).some(
    (val: any) => isAfterCurrentTime(val.original.actualStartTime),
  );
  /** Sync form values when active step changes */
  useEffect(() => {
    if (worksheetFields.length > 0 && activeStep < worksheetFields.length) {
      const currentWorksheet = {
        ...worksheetFields[activeStep],
      };

      if (currentWorksheet) {
        // Force update the form values for the current worksheet
        setValue(`${currentPath}.startTime`, currentWorksheet.startTime, {
          shouldDirty: true,
          shouldValidate: false,
        });
        setValue(`${currentPath}.endTime`, currentWorksheet.endTime, {
          shouldDirty: true,
          shouldValidate: false,
        });
        setValue(`${currentPath}.notes`, currentWorksheet.notes || '', {
          shouldDirty: true,
          shouldValidate: false,
        });
        setValue(
          `${currentPath}.extendShifts`,
          currentWorksheet.extendShifts || [],
          { shouldDirty: true, shouldValidate: false },
        );
        setValue(
          `${currentPath}.locationOverride`,
          currentWorksheet.locationOverride || undefined,
          { shouldDirty: true, shouldValidate: false },
        );
      }
    }
  }, [activeStep, worksheetFields, currentPath, setValue, shiftType]);

  /** Bulk quantity update for current worksheet */
  const handleBulkChange = (delta: 1 | -1) => {
    const path = `${currentPath}.shifts` as const;
    const path1 = `${currentPath}.originalShifts` as const;
    const rows = watch(path) || [];
    const rows1 = watch(path) || [];

    const updated = rows.map((r: WorksheetForm['shifts'][number]) => {
      const isEditableRow =
        isFixedCost || isAfterCurrentTime(r.original.actualStartTime);
      if (!isEditableRow) return r;

      const shiftIndex = current.originalShifts.findIndex(
        s => s.shiftId === r.shiftId,
      );
      const path1 =
        `${currentPath}.originalShifts.${shiftIndex}.updatedUnits` as const;
      setValue(path1, watch(path1) + delta, { shouldDirty: true });
      return {
        ...r,
        updatedUnits: Math.max(1, (r.updatedUnits || 1) + delta),
        totalUnits: Math.max(1, (r.totalUnits || 1) + delta),
      };
    });

    setValue(path, updated, { shouldDirty: true });

    const bulkVal = watch(`${currentPath}.bulkUpdate` as any);
    if (typeof bulkVal === 'number' && bulkVal >= 1) {
      setValue(`${currentPath}.bulkUpdate` as any, bulkVal + delta, {
        shouldDirty: true,
      });
    }
  };

  //max date
  const [reRenderKey, setReRenderKey] = useState(0);
  /** Per shift quantity */
  const handleShiftQty = (
    shiftIndex: number,
    delta: 1 | -1,
    shiftId: string,
  ) => {
    const shiftIndexOfOriginalShift = current.originalShifts.findIndex(
      s => s.shiftId === shiftId,
    );

    const path = `${currentPath}.shifts.${shiftIndex}.updatedUnits` as const;
    const path1 = `${currentPath}.shifts.${shiftIndex}.totalUnits` as const;
    const path2 =
      `${currentPath}.originalShifts.${shiftIndexOfOriginalShift}.updatedUnits` as const;
    const cur = watch(path) || 1;
    const cur1 = watch(path1) || 1;
    const cur2 = watch(path2) || 1;
    setValue(path, Math.max(1, cur + delta), { shouldDirty: true });
    setValue(path1, Math.max(1, cur1 + delta), { shouldDirty: true });
    setValue(path2, Math.max(1, cur2 + delta), { shouldDirty: true });
  };

  /** Time validation per worksheet */

  const onUpdateTime = (
    newTime: Date,
    type: 'startTime' | 'endTime',
    wIndex: number,
    various?: boolean,
  ): boolean => {
    const wsPath = `worksheets.${wIndex}` as const;
    const shifts = (watch(`${wsPath}.shifts`) || []).map(
      (s: any) => s.original,
    ) as Shift[];

    if (!shifts?.length) return false;

    const currentStart = getValues(`${wsPath}.startTime`);
    const currentEnd = getValues(`${wsPath}.endTime`);
    const newTimeDayjs = dayjs(newTime);

    const otherTime = type === 'startTime' ? currentEnd : currentStart;

    const startFull =
      type === 'startTime'
        ? newTimeDayjs
        : currentStart
          ? dayjs(currentStart)
          : null;
    const endInput =
      type === 'endTime'
        ? newTimeDayjs
        : otherTime
          ? dayjs(otherTime)
          : null;

    // ---- Duration rule (max 16h unless Swing Shift) ----
    if (startFull && endInput) {
      const endAligned = alignShiftEndToStart(startFull, endInput);
      const durationMins = endAligned.diff(startFull, 'minute');
      const durationHrs = durationMins / 60;

      if (durationHrs > 16 && !various) {
        notify(
          `Shift duration (${durationHrs.toFixed(1)}h) exceeds 16 hour limit`,
          'error',
        );
        return false;
      }
    }

    // ---- Shift type rules ----
    const shiftType = determineShiftType(shifts);
    const mins = newTimeDayjs.hour() * 60 + newTimeDayjs.minute();
    const cutoff = 15 * 60; // 3 PM

    // if (shiftType === 'Day' && type === 'startTime' && mins >= cutoff) {
    //   notify('Day shifts must start before 3 PM', 'error');
    //   return false;
    // }
    // if (shiftType === 'Night' && type === 'startTime' && mins < cutoff) {
    //   notify('Night shifts must start at or after 3 PM', 'error');
    //   return false;
    // }

    // ---- Save value if all checks pass ----
    if (type === 'startTime') {
      setValue(`${wsPath}.startTime`, newTimeDayjs, { shouldDirty: true });
      if (currentEnd) {
        setValue(
          `${wsPath}.endTime`,
          alignShiftEndToStart(newTimeDayjs, dayjs(currentEnd)),
          { shouldDirty: true },
        );
      }
    } else if (startFull) {
      setValue(
        `${wsPath}.endTime`,
        alignShiftEndToStart(startFull, newTimeDayjs),
        { shouldDirty: true },
      );
    } else {
      setValue(`${wsPath}.endTime`, newTimeDayjs, { shouldDirty: true });
    }
    return true;
  };

  const validateExtendDate = (
    date: Dayjs | null,
    type: 'startDate' | 'endDate',
    idx: number,
    setValue: any,
    extendFields: any,
  ): boolean => {
    if (!date) return true;
    const newStart =
      type === 'startDate'
        ? date
        : extendFields[idx]?.startDate
        ? dayjs(extendFields[idx].startDate)
        : undefined;

    const newEnd =
      type === 'endDate'
        ? date
        : extendFields[idx]?.endDate
        ? dayjs(extendFields[idx].endDate)
        : undefined;

    const today = dayjs().startOf('day');

    // 1️ Cannot be before today
    if (date.isBefore(today, 'day')) {
      notify('Date cannot be before today', 'error');
      return false;
    }

    // 2️ End must be >= start
    if (newStart && newEnd && newEnd.isBefore(newStart, 'day')) {
      notify('End date cannot be before start date', 'error');
      return false;
    }
    const shiftStart = dayjs(
      product?.products?.find(
        (val: any) => val?.cartId === watch(`${currentPath}.worksheet`)?.cartId,
      )?.startTime,
    );
    const shiftEnd = dayjs(
      product?.products?.find(
        (val: any) => val?.cartId === watch(`${currentPath}.worksheet`)?.cartId,
      )?.endTime,
    );

    // Condition 1:
    if (
      (newStart &&
        (newStart.isBefore(shiftEnd, 'day') ||
          newStart.isSame(shiftEnd, 'day')) &&
        !newStart.isBefore(shiftStart, 'day')) ||
      (newEnd &&
        (newEnd.isAfter(shiftStart, 'day') ||
          newEnd.isSame(shiftStart, 'day')) &&
        !newEnd.isBefore(shiftStart, 'day') &&
        !newEnd.isAfter(shiftEnd, 'day'))
    ) {
      notify('Date cannot be between start and end date', 'error');
      return false;
    }

    // Condition 2:
    if (
      newStart &&
      newStart.isBefore(shiftStart, 'day') &&
      newEnd &&
      newEnd.isAfter(shiftEnd, 'day')
    ) {
      notify('End date cannot be after service start date.', 'error');
      return false;
    }

    // Optional: generic overlap fallback
    if (
      newStart &&
      newEnd &&
      newStart.isSameOrBefore(shiftEnd, 'day') &&
      newEnd.isSameOrAfter(shiftStart, 'day')
    ) {
      notify('Date overlaps with existing service dates', 'error');
      return false;
    }

    // 4️ Check overlap with other extension ranges
    for (let i = 0; i < extendFields.length; i++) {
      if (i === idx) continue;

      const extStart = extendFields[i]?.startDate
        ? dayjs(extendFields[i].startDate)
        : null;

      const extEnd = extendFields[i]?.endDate
        ? dayjs(extendFields[i].endDate)
        : null;

      if (!extStart || !extEnd || !newStart || !newEnd) continue;

      const isOverlap =
        newStart.isSameOrBefore(extEnd, 'day') &&
        newEnd.isSameOrAfter(extStart, 'day');

      if (isOverlap) {
        notify('Extension dates cannot overlap each other', 'error');
        return false;
      }
    }

    return true;
  };

  /** Extend service helpers per worksheet */
  const ensureFirstExtendRow = (wIndex: number) => {
    const rows = watch(`worksheets.${wIndex}.extendShifts`) || [];

    if (rows.length === 0) {
      const latestLastShift = product?.products?.find(
        (val: any) => val?.cartId === watch(`${currentPath}.worksheet`)?.cartId,
      )?.endTime;
      // 3️ Add 1 day (or whatever unit you want)
      const lastEndTime = dayjs(latestLastShift).add(1, 'day');

      // 4️ Set extendShifts
      setValue(
        `worksheets.${wIndex}.extendShifts`,
        [{ startDate: lastEndTime, endDate: undefined }],
        { shouldDirty: true },
      );
    }
  };

  /** Save current step data before navigation */
  const saveCurrentStepData = () => {
    if (worksheetFields.length === 0 || activeStep >= worksheetFields.length)
      return;

    const currentData = watch(currentPath);
    if (!currentData) return;

    // Full deep update via setValue (forces RHF re-render)
    setValue(
      currentPath,
      {
        ...worksheetFields[activeStep],
        ...currentData, // Overwrite with watched (live) values
        shifts:
          currentData.shifts?.map(s => ({
            ...s,
            updatedUnits: s.updatedUnits || 1, // Ensure bundling sticks
            totalUnits: s.totalUnits || 1,
          })) || [],
        originalShifts:
          currentData.originalShifts?.map(s => ({
            ...s,
            updatedUnits: s.updatedUnits || 1,
          })) || [],
      },
      { shouldValidate: false, shouldDirty: true },
    );

    // Trigger array update as fallback
    updateWorksheet(activeStep, getValues(currentPath));
  };

  /** Submit handlers */
  const buildPayload = (data: EditFormValues) => {
    const editItems = data.worksheets.map(w => {
      const firstShift = w.originalShifts[0]?.original;
      const worksheet = w.worksheet as any;

      // Original start and end times (ISO strings)
      const originalStartTime =
        firstShift?.actualStartTime || worksheet?.firstShift;
      const originalEndTime = firstShift?.actualEndTime || worksheet?.lastShift;

      // Current updated times in millis
      const updatedStartTimeMs = w.startTime?.valueOf();
      const updatedEndTimeMs = w.endTime?.valueOf();

      // Check if start or end time changed
      const startTimeChanged =
        updatedStartTimeMs &&
        updatedStartTimeMs !== new Date(originalStartTime).getTime();
      const endTimeChanged =
        updatedEndTimeMs &&
        updatedEndTimeMs !== new Date(originalEndTime).getTime();

      const timesChanged = startTimeChanged || endTimeChanged;

      return {
        shifts: w.originalShifts.map(s => ({
          shiftId: s.shiftId,
          updatedUnits: s.updatedUnits || 1,
        })),
        oldSetId: firstShift?.deliveryAddress?.setId || worksheet?.setId,
        newSetId: firstShift?.deliveryAddress?.setId || worksheet?.setId,
        productionId:
          firstShift?.deliveryAddress?.productionId || worksheet?.productionId,
        productionName:
          firstShift?.deliveryAddress?.productionName ||
          worksheet?.productionName,
        productionType:
          firstShift?.deliveryAddress?.productionType || 'Commercial',
        newSetName: firstShift?.deliveryAddress?.setName || worksheet?.setName,
        cartId: firstShift?.cartId || worksheet?.cartId,
        ...(timesChanged && {
          updatedStartTime: updatedStartTimeMs,
          updatedEndTime: updatedEndTimeMs,
        }),
        ...(w.notes && {
          notes: {
            note: w.notes,
            noteFor: 'PUBLIC',
          },
        }),
        ...(w.locationOverride && {
          updatedLocations: [
            ...w.originalShifts?.map(val => ({
              oldLocationId: val.original.deliveryAddress?.locationId,
              newLocationId: w.locationOverride?.value,
            })),
          ],
        }),
        ...(w.extendShifts &&
          w.extendShifts.length > 0 && {
            extendService: {
              isExtendService: true,
              dateRanges: w.extendShifts.map(r => ({
                startTime: dayjs(r.startDate).startOf('day').valueOf(),
                endTime: dayjs(r.endDate).endOf('day').valueOf(),
              })),
            },
          }),
      };
    });

    const timedWorksheet = data.worksheets.find(w => w.startTime || w.endTime);
    const startOffset = timedWorksheet?.startTime
      ? dayjs(timedWorksheet.startTime).toDate().getTimezoneOffset()
      : new Date().getTimezoneOffset();
    const endOffset = timedWorksheet?.endTime
      ? dayjs(timedWorksheet.endTime).toDate().getTimezoneOffset()
      : startOffset;

    return {
      offset: startOffset,
      startOffset,
      endOffset,
      editItems,
    };
  };

  const onSubmit = (values: EditFormValues) => {
    const payload = buildPayload(values);
    const extendedItem = payload.editItems.find(item => item.extendService);

    let endDateObj = null;
    let startDateObj = null;
    if (extendedItem && extendedItem.extendService?.dateRanges?.length > 0) {
      const extendedEndDate = extendedItem.extendService.dateRanges[0].endTime;
      const extendedStartDate =
        extendedItem.extendService.dateRanges[0].startTime;
      endDateObj = extendedEndDate
        ? new Date(extendedEndDate).toISOString()
        : null;
      startDateObj = extendedStartDate
        ? new Date(extendedStartDate).toISOString()
        : null;
    }
    setIsSubmitting(true);
    updateShiftPreview(
      payload,
      'fromCart',
      () => {
        track(ANALYTICS_EVENTS.CART_SHIFT_UPDATED);
        navigate(`/${ROUTES.shiftDetailCart}`, {
          replace: true,
          state: {
            product: {
              ...product,
              products: product.products.map((p: any) =>
                p.cartId == selectedWorksheet?.cartId
                  ? {
                      ...p,
                      // Only add endTime and starttime if the service was extended
                      ...(endDateObj && dayjs(p.endTime).isBefore(endDateObj)
                        ? { endTime: endDateObj }
                        : {}),
                      ...(startDateObj &&
                      dayjs(p.startTime).isAfter(startDateObj)
                        ? { startTime: startDateObj }
                        : {}),
                    }
                  : p,
              ),
            },
          },
        });
        setSelectedDate(null);
        handleClose();
        setIsSubmitting(false);
      },
      () => {
        setIsSubmitting(false);
      },
    );
  };
  const handleClose = () => {
    setPickerOpen({
      endPickerOpen: false,
      startpickerOpen: false,
      endTextField: false,
      starttextField: false,
      isEndAccepted: false,
      isStartAccepted: false,
    });
    onClose();
    setWarningOpen(false);
    setShiftType('');
  };

  const locationOverride = watch(`${currentPath}.locationOverride`);
  const locationLabel =
    locationOverride?.label || (current?.worksheet as any)?.locationName;
  const locationAddress =
    locationOverride?.address || (current?.worksheet as any)?.address;
  // Handle location purpose from override
  const overridePurposes = locationOverride?.locationPurpose || [];

  // Find worksheet location details
  const worksheetLocation = current?.worksheet?.shifts?.find(
    (val: any) => val?.locationDetails?._id === current?.worksheet?.locationId,
  )?.locationDetails;

  const worksheetPurposes = worksheetLocation?.locationPurpose || [];

  // Determine which purposes to show
  const purposesToShow = overridePurposes.length
    ? overridePurposes
    : worksheetPurposes;

  const remainingCount =
    purposesToShow.length > 1 ? purposesToShow.length - 1 : 0;

  // Build final display text (match MyOrders format: label - purpose)
  const locationDisplay = `${locationLabel || ''} - ${purposesToShow[0]} `;

  if (!open) return null;
  if (!worksheetFields.length)
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        className="modal-main edit-shift-modal"
      >
        <div className="modal-dialog md-sm">
          <div className="modal-content">
            <div className="modal-title d-flex align-center flex-between">
              <Typography variant="h6" fontWeight={600}>
                Edit Shift
              </Typography>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </div>
            <div className="modal-body pt-20">
              <Typography color="text.secondary">
                No worksheets selected.
              </Typography>
            </div>
            <div className="modal-actions">
              <Button onClick={handleClose} variant="outlined">
                Close
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    );

  /** Current worksheet-specific handles */
  const extendFields = watch(`${currentPath}.extendShifts`) || [];

  const wsErrors = (errors?.worksheets?.[activeStep] || {}) as any;

  // const maxEndDate = React.useMemo(() => {
  //   if (!extendFields.length) return null;
  //   return extendFields.reduce((max: Dayjs | null, item: any) => {
  //     if (item.endDate && (!max || item.endDate.isAfter(max))) {
  //       return item.endDate;
  //     }
  //     return max;
  //   }, null);
  // }, [extendFields]);

  // removed console logs

  const showTimeColumn = !current?.worksheet?.rentPricing?.isFlexibleShiftHr;
  return (
    <Dialog open={open} onClose={handleClose} fullWidth className="modal-main edit-shift-modal">
      <div className="modal-dialog md-sm">
        <div className="modal-content">
          <div className="modal-title d-flex align-center flex-between">
            <Typography variant="h6" fontWeight={600}>
              Edit
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <form onSubmit={handleSubmit(onSubmit as any)}>
            <div className="modal-body pt-20">
              <div className="body-scroll-sm">
                {/* --------- Service header --------- */}
                <div className="mb-24">
                  {worksheetFields.length > 1 && (
                    <div className="order-stepper">
                      <p className="text-white">Selected Items</p>
                      <p className="text-white">{`${activeStep + 1}/${
                        worksheetFields?.length
                      }`}</p>
                    </div>
                  )}
                </div>

                <div className="card-grid border">
                  <img
                    src={
                      (current?.worksheet as any)?.productInfo?.thumbnailImg ||
                      Images.AUTH_BG_1
                    }
                    alt={
                      (current?.worksheet as any)?.productInfo?.title ||
                      'Service'
                    }
                  />
                  <div className="pl-12">
                    <h2 className="fw600 MuiTypography-subtitle1 mb-8">
                      {(current?.worksheet as any)?.productInfo?.title}
                    </h2>
                    {/* <div className="d-flex">
                      <i className="pin-icon mr-4"></i>
                      <p className="MuiTypography-subtitle text-text">
                        {(current?.worksheet as any)?.locationName}
                      </p>
                    </div> */}
                  </div>
                </div>

                {/* --------- Location row --------- */}
                <div className="card-grid">
                  <span className="icon-round blue border">
                    <i className="set-white-icon"></i>
                  </span>
                  <div className="pl-12 d-flex flex-between">
                    <div>
                      <div className="d-flex mb-8 flex-between">
                        <p className="MuiTypography-body2 fw600">
                          {(current?.worksheet as any)?.setName.toUpperCase()}
                        </p>
                      </div>
                      <p className="MuiTypography-subtitle1 text-text trunc">
                        {purposesToShow?.[0]}
                        {remainingCount > 0 && (
                          <ToolTipComponent
                            theme="dark"
                            title={
                              <div>
                                {purposesToShow.map(
                                  (purpose: string, index: number) => {
                                    if (index == 0) return null;
                                    return <div key={index}>{purpose}</div>;
                                  },
                                )}
                              </div>
                            }
                          >
                            <span className="cursor-pointer">
                              {' '}
                              + {remainingCount}
                            </span>
                          </ToolTipComponent>
                        )}{' '}
                        : {locationLabel}, {locationAddress}
                      </p>
                    </div>

                    {(isFixedCost || hasAnyFutureShift) && (
                      <IconButton
                        onClick={() => setOpenLocationUpdateModal(true)}
                      >
                        <img
                          src={Images.EDIT_BLACK}
                          alt="Edit"
                          style={{ height: 24, width: 24 }}
                        />
                      </IconButton>
                    )}
                  </div>
                </div>

                {/* Shift Type Selection */}
                {/* {showTimeColumn && <div className="mb-24 pt-10 border-bottom">
                  <p className="MuiTypography-subtitle1 mb-8 fw500">
                    Shift Type
                  </p>
                  <RadioGroup
                    value={
                      shiftType
                        ? shiftType
                        : watch(`${currentPath}.originalCoverage`)
                    }
                    onChange={e => setShiftType(e.target.value)}
                    row
                    className="radio-regular"
                  >
                    <FormControlLabel
                      value="Day"
                      control={<Radio />}
                      label="Shift - Day"
                      disabled={
                        watch(`${currentPath}.originalCoverage`) != 'All'
                      }
                    />
                    <FormControlLabel
                      value="Night"
                      control={<Radio />}
                      label="Shift - Night"
                      disabled={
                        watch(`${currentPath}.originalCoverage`) != 'All'
                      }
                    />
                    <FormControlLabel
                      value="All"
                      control={<Radio />}
                      label="All Shift"
                      disabled={
                        watch(`${currentPath}.originalCoverage`) != 'All'
                      }
                    />
                  </RadioGroup>
                </div>} */}

                {/* --------- Shift schedule quantities --------- */}
                <div className="mb-24 border-bottom">
                  <p className="MuiTypography-subtitle1 mb-8 fw500">
                    Shift Schedule
                  </p>

                  {/* Bulk edit only when > 1 row */}
                  {(watch(`${currentPath}.shifts`) || []).length > 1 && (
                    <div className="shift-bg-blue lg mb-15">
                      <p className="MuiTypography-subtitle1 mb-4">
                        Bulk Edit Quantity
                      </p>
                      <Grid container spacing={1}>
                        <Grid item xs={10}>
                          <p className="MuiTypography-subtitle3 text-text lh-14">
                            Adjusting this value will uniformly increase or
                            decrease the quantities for all dates below.This
                            means every quantity on each date will shift forward
                            or backward by the same amount.
                          </p>
                        </Grid>
                        <Grid item xs={2}>
                          <div className="quantity-shift">
                            <button
                              className="blank-btn"
                              onClick={e => {
                                e.preventDefault();
                                const bulk = watch(
                                  `${currentPath}.bulkUpdate` as any,
                                );
                                const shiftsArr = watch(
                                  `${currentPath}.shifts` as any,
                                );
                                if (
                                  watch(`${currentPath}.bulkUpdate`) <= 1 ||
                                  (!isFixedCost &&
                                    watch(`${currentPath}.shifts`).every(
                                      val =>
                                        !isAfterCurrentTime(
                                          val.original.actualStartTime,
                                        ),
                                    ))
                                ) {
                                  return;
                                }

                                handleBulkChange(-1);
                              }}
                              type="button"
                              disabled={
                                !isFixedCost &&
                                watch(`${currentPath}.shifts`).every(
                                  val =>
                                    !isAfterCurrentTime(
                                      val.original.actualStartTime,
                                    ),
                                )
                              }
                            >
                              <i className="minus-icon"></i>
                            </button>
                            <p>
                              {(typeof watch(
                                `${currentPath}.bulkUpdate` as any,
                              ) === 'number'
                                ? watch(`${currentPath}.bulkUpdate` as any)
                                : 1) - 1}
                            </p>
                            <IconButton
                              className="blank-btn"
                              onClick={e => {
                                e.preventDefault();
                                const shiftsForPlus = watch(
                                  `${currentPath}.shifts` as any,
                                );
                                if (
                                  !isFixedCost &&
                                  watch(`${currentPath}.shifts`).every(
                                    val =>
                                      !isAfterCurrentTime(
                                        val.original.actualStartTime,
                                      ),
                                  )
                                ) {
                                  return;
                                }
                                handleBulkChange(1);
                              }}
                              type="button"
                              disabled={
                                !isFixedCost &&
                                watch(`${currentPath}.shifts`).every(
                                  val =>
                                    !isAfterCurrentTime(
                                      val.original.actualStartTime,
                                    ),
                                )
                              }
                            >
                              <i className="plus-icon"></i>
                            </IconButton>
                          </div>
                        </Grid>
                      </Grid>
                    </div>
                  )}

                  <div className="shift-grid">
                    {/* header */}
                    <Grid container spacing={1} className="mb-8">
                      <Grid item xs={showTimeColumn ? 2.5 : 3.5}>
                        <p className="MuiTypography-subtitle1 text-text">
                          Date
                        </p>
                      </Grid>

                      {showTimeColumn && (
                        <Grid item xs={4}>
                          <p className="MuiTypography-subtitle1 text-text">
                            Time Range
                          </p>
                        </Grid>
                      )}

                      <Grid item xs={showTimeColumn ? 3.5 : 5}>
                        <p className="MuiTypography-subtitle1 text-text">
                          Location
                        </p>
                      </Grid>

                      <Grid item xs={1.5}>
                        <p className="MuiTypography-subtitle1 text-text">Qty</p>
                      </Grid>
                    </Grid>

                    {/* rows */}
                    {(watch(`${currentPath}.shifts`) || []).map(
                      (row: any, idx: number) => (
                        <Grid
                          container
                          spacing={1}
                          key={row.shiftId}
                          className="align-unset mb-12"
                        >
                          {/* DATE */}
                          <Grid item xs={showTimeColumn ? 2.6 : 3}>
                            <p className="MuiTypography-subtitle1 text-primary_blue_500 fw400 shift-bg-blue align-center d-flex">
                              {moment(row.original.actualStartTime).format(
                                'ddd, MMM D',
                              )}
                            </p>
                          </Grid>

                          {/* TIME (conditional) */}
                          {showTimeColumn && (
                            <Grid item xs={4.5}>
                              <div className="d-flex shift-bg-blue align-center">
                                <p className="MuiTypography-subtitle1 text-primary_blue_500">
                                  {moment(row.original.actualStartTime).format(
                                    'h:mm A',
                                  )}
                                  {' - '}
                                  {moment(row.original.actualEndTime).format(
                                    'h:mm A',
                                  )}
                                  ({getCoverage(row.original.actualStartTime)})
                                </p>
                              </div>
                            </Grid>
                          )}

                          {/* LOCATION */}
                          <Grid item xs={showTimeColumn ? 2.9 : 5}>
                            <div className="d-flex shift-bg-blue align-center">
                              <i className="pin-icon mr-6"></i>

                              <ToolTipComponent
                                theme="dark"
                                title={`${
                                  dayjs(
                                    row.original.actualStartTime,
                                  ).isSameOrBefore(dayjs())
                                    ? isFixedCost
                                      ? watch(
                                          `${currentPath}.locationOverride`,
                                        )?.locationPurpose?.join(', ') ||
                                        row?.original?.deliveryAddress?.locationPurpose?.join(
                                          ', ',
                                        )
                                      : row?.original?.deliveryAddress?.locationPurpose?.join(
                                          ', ',
                                        )
                                    : watch(
                                        `${currentPath}.locationOverride`,
                                      )?.locationPurpose?.join(', ') ||
                                      row?.original?.deliveryAddress?.locationPurpose?.join(
                                        ', ',
                                      )
                                }${
                                  dayjs(
                                    row.original.actualStartTime,
                                  ).isSameOrBefore(dayjs())
                                    ? isFixedCost
                                      ? ' : ' +
                                        locationLabel +
                                        ', ' +
                                        locationAddress
                                      : ' : ' +
                                        row?.original?.deliveryAddress
                                          ?.locationName +
                                        ', ' +
                                        row?.original?.deliveryAddress?.address
                                    : ' : ' +
                                      locationLabel +
                                      ', ' +
                                      locationAddress
                                }`}
                              >
                                <p className="MuiTypography-body2 text-gray_900 trunc">
                                  <span>
                                    {dayjs(
                                      row?.original?.actualStartTime,
                                    ).isSameOrBefore(dayjs())
                                      ? isFixedCost
                                        ? watch(
                                            `${currentPath}.locationOverride`,
                                          )?.locationPurpose?.[0] ||
                                          row?.original?.deliveryAddress
                                            ?.locationPurpose?.[0]
                                        : row?.original?.deliveryAddress
                                            ?.locationPurpose?.[0]
                                      : watch(`${currentPath}.locationOverride`)
                                          ?.locationPurpose?.[0] ||
                                        row?.original?.deliveryAddress
                                          ?.locationPurpose?.[0]}
                                  </span>
                                  {dayjs(
                                    row.original.actualStartTime,
                                  ).isSameOrBefore(dayjs())
                                    ? isFixedCost
                                      ? ' : ' +
                                        locationLabel +
                                        ', ' +
                                        locationAddress
                                      : ' : ' +
                                        row?.original?.deliveryAddress
                                          ?.locationName +
                                        ', ' +
                                        row?.original?.deliveryAddress?.address
                                    : ' : ' +
                                      locationLabel +
                                      ', ' +
                                      locationAddress}
                                </p>
                              </ToolTipComponent>
                            </div>
                          </Grid>

                          {/* QTY */}
                          <Grid item xs={1.5}>
                            <div className="quantity-shift">
                              <IconButton
                                className="blank-btn"
                                onClick={e => {
                                  e.preventDefault();
                                  handleShiftQty(idx, -1, row.shiftId);
                                }}
                                disabled={
                                  row.totalUnits === row.initialUnits ||
                                  (!current?.worksheet?.rentPricing
                                    ?.isFlexibleShiftHr &&
                                    dayjs(
                                      row.original.actualStartTime,
                                    ).isSameOrBefore(dayjs()))
                                }
                              >
                                <i className="minus-icon"></i>
                              </IconButton>

                              <p>{row.totalUnits || 1}</p>

                              <IconButton
                                onClick={e => {
                                  e.preventDefault();
                                  handleShiftQty(idx, 1, row.shiftId);
                                }}
                                className="blank-btn"
                                disabled={
                                  !current?.worksheet?.rentPricing
                                    ?.isFlexibleShiftHr &&
                                  dayjs(
                                    row.original.actualStartTime,
                                  ).isSameOrBefore(dayjs())
                                }
                              >
                                <i className="plus-icon"></i>
                              </IconButton>
                            </div>
                          </Grid>
                        </Grid>
                      ),
                    )}
                  </div>
                  {current?.worksheet?.rentPricing?.isFlexibleShiftHr &&
                    current?.worksheet?.rentPricing?.minRateModel ==
                      'WEEKLY' && (
                      <div
                        className="grey-box yellow d-flex align-start mb-24"
                        style={{
                          border: '1px solid var(--warning_500)',
                          borderRadius: '12px',
                          padding: '12px 16px',
                        }}
                      >
                        <i
                          className="info-icon mr-6"
                          style={{
                            flexShrink: 0,
                            filter:
                              'brightness(0) saturate(100%) invert(33%) sepia(26%) saturate(3764%) hue-rotate(358deg) brightness(90%) contrast(96%)',
                          }}
                        />
                        <p className="MuiTypography-subtitle1 mb-0 lh-14">
                          Are you sure? Increasing the quantity on a single day
                          will result in an additional week charge. To add a
                          single unit only, add a new service at the day rate.
                        </p>
                      </div>
                    )}
                </div>

                {/* --------- Time pickers per worksheet --------- */}
                <div className="mb-24">
                  {!current?.worksheet?.rentPricing?.isFlexibleShiftHr && (
                    <Grid container spacing={1}>
                      <Grid item md={6} xs={12}>
                        <div className="form-in lg">
                          <Controller
                            name={`${currentPath}.startTime`}
                            control={control}
                            render={({ field }) => {
                              const shifts =
                                watch(`${currentPath}.shifts`) || [];
                              const startVaries = getTimeDisplay(
                                shifts,
                                false,
                              ).startVaries;
                              const endVaries = getTimeDisplay(
                                shifts,
                                false,
                              ).endVaries;
                              const storedStartTime = watch(
                                `${currentPath}.startTime`,
                              );
                              const allCoverage = watch(
                                `${currentPath}.shifts`,
                              ).map(val =>
                                getCoverage(val?.original.actualStartTime),
                              );
                              const disabled =
                                allCoverage?.length > 1
                                  ? new Set(allCoverage).size > 1
                                  : false;

                              if (startVaries) {
                                // Show TextField when picker is closed, otherwise show TimePicker
                                if (!pickerOpen.starttextField) {
                                  return (
                                    <FormControl>
                                      <FormLabel className="formLabel">
                                        Start Time <sup>*</sup>
                                      </FormLabel>
                                      <TextField
                                        value="Various"
                                        disabled={disabled}
                                        InputProps={{
                                          readOnly: true,
                                          endAdornment: (
                                            <div className="inputEndIcon">
                                              <IconButton
                                                disabled={disabled}
                                                onClick={() =>
                                                  setPickerOpen(prev => ({
                                                    ...prev,
                                                    starttextField: true,
                                                    startpickerOpen: true,
                                                  }))
                                                }
                                              >
                                                <AccessTimeIcon fontSize="small" />
                                              </IconButton>
                                            </div>
                                          ),
                                        }}
                                        onClick={() => {
                                          if (disabled) return;
                                          setPickerOpen(prev => ({
                                            ...prev,
                                            starttextField: true,
                                            startpickerOpen: true,
                                          }));
                                        }}
                                        fullWidth
                                        style={{ cursor: 'pointer' }}
                                      />
                                    </FormControl>
                                  );
                                } else {
                                  // Render TimePicker *only* when pickerOpen is true, replacing TextField completely
                                  return (
                                    <TimePickerControlledAny
                                      key={`start-time-${activeStep}-${reRenderKey}`}
                                      control={control}
                                      id={`start-time-${activeStep}`}
                                      minutesStep={TIME_PICKER_MINUTES_STEP}
                                      open={pickerOpen.startpickerOpen}
                                      disabled={disabled}
                                      onClose={() => {
                                        setPickerOpen(prev => ({
                                          ...prev,
                                          startpickerOpen: false,
                                          starttextField: prev.isStartAccepted
                                            ? true
                                            : false,
                                        }));
                                      }}
                                      onClick={() =>
                                        setPickerOpen(prev => ({
                                          ...prev,
                                          startpickerOpen: true,
                                        }))
                                      }
                                      label="Start Time"
                                      name={`${currentPath}.startTime`}
                                      placeholder="Select Start Time"
                                      validate
                                      error={!!wsErrors?.startTime}
                                      helperText={wsErrors?.startTime?.message}
                                      requiredField
                                      value={
                                        pickerOpen.isStartAccepted
                                          ? storedStartTime
                                          : null
                                      }
                                      date={storedStartTime}
                                      onChange={(val: any) => {
                                        if (val?.toDate) {
                                          const ok = onUpdateTime(
                                            val.toDate(),
                                            'startTime',
                                            activeStep,
                                            true,
                                          );
                                          const oldVal = watch(
                                            `${currentPath}.startTime`,
                                          );
                                          if (ok) {
                                            setValue(
                                              `${currentPath}.startTime`,
                                              val,
                                            );
                                          } else {
                                            setValue(
                                              `${currentPath}.startTime`,
                                              oldVal,
                                            );
                                            setReRenderKey(prev => prev + 1);
                                          }
                                        } else {
                                          setValue(
                                            `${currentPath}.startTime`,
                                            null,
                                          );
                                        }

                                        setPickerOpen(prev => ({
                                          ...prev,
                                          isStartAccepted: true,
                                        }));
                                      }}
                                    />
                                  );
                                }
                              } else {
                                // When times are not various, always show the TimePicker inline
                                return (
                                  <TimePicker
                                    key={`start-time-${activeStep}-${reRenderKey}`}
                                    control={control}
                                    disabled={disabled}
                                    id={`start-time-${activeStep}`}
                                    minutesStep={TIME_PICKER_MINUTES_STEP}
                                    label="Start Time"
                                    name={`${currentPath}.startTime`}
                                    placeholder="Select Start Time"
                                    validate
                                    error={!!wsErrors?.startTime}
                                    helperText={wsErrors?.startTime?.message}
                                    requiredField
                                    value={storedStartTime ?? null}
                                    onChange={(val: any) => {
                                      if (val?.toDate) {
                                        const ok = onUpdateTime(
                                          val.toDate(),
                                          'startTime',
                                          activeStep,
                                          endVaries,
                                        );
                                        const oldVal = watch(
                                          `${currentPath}.startTime`,
                                        );
                                        if (ok) {
                                          setValue(
                                            `${currentPath}.startTime`,
                                            val,
                                          );
                                        } else {
                                          setValue(
                                            `${currentPath}.startTime`,
                                            oldVal,
                                          );
                                          setReRenderKey(prev => prev + 1);
                                        }
                                      } else {
                                        field.onChange(val);
                                      }
                                    }}
                                  />
                                );
                              }
                            }}
                          />
                        </div>
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <div className="form-in lg">
                          <Controller
                            name={`${currentPath}.endTime`}
                            control={control}
                            render={({ field }) => {
                              const shifts =
                                watch(`${currentPath}.shifts`) || [];
                              const endVaries = getTimeDisplay(
                                shifts,
                                false,
                              ).endVaries;
                              const startVaries = getTimeDisplay(
                                shifts,
                                false,
                              ).startVaries;
                              const storedEndTime = watch(
                                `${currentPath}.endTime`,
                              );
                              const oldVal = storedEndTime;
                              const allCoverage = watch(
                                `${currentPath}.shifts`,
                              ).map(val =>
                                getCoverage(val?.original.actualStartTime),
                              );
                              const disabled =
                                allCoverage?.length > 1
                                  ? new Set(allCoverage).size > 1
                                  : false;
                              if (endVaries) {
                                // When end time varies, show a read-only 'Various' TextField
                                // and open the TimePicker modal on click (similar to startTime pattern)
                                if (!pickerOpen.endTextField) {
                                  return (
                                    <FormControl>
                                      <FormLabel className="formLabel">
                                        End Time <sup>*</sup>
                                      </FormLabel>
                                      <TextField
                                        value="Various"
                                        InputProps={{
                                          readOnly: true,
                                          endAdornment: (
                                            <div className="inputEndIcon">
                                              <IconButton
                                                onClick={() =>
                                                  setPickerOpen(prev => ({
                                                    ...prev,
                                                    endPickerOpen: true,
                                                    endTextField: true,
                                                  }))
                                                }
                                                disabled={disabled}
                                              >
                                                <AccessTimeIcon fontSize="small" />
                                              </IconButton>
                                            </div>
                                          ),
                                        }}
                                        onClick={() => {
                                          if (disabled) return;
                                          setPickerOpen(prev => ({
                                            ...prev,
                                            endPickerOpen: true,
                                            endTextField: true,
                                          }));
                                        }}
                                        fullWidth
                                        style={{ cursor: 'pointer' }}
                                        disabled={disabled}
                                      />
                                    </FormControl>
                                  );
                                }

                                return (
                                  <TimePickerControlledAny
                                    key={`end-time-${activeStep}-${reRenderKey}`}
                                    control={control}
                                    id={`end-time-${activeStep}`}
                                    minutesStep={TIME_PICKER_MINUTES_STEP}
                                    disabled={disabled}
                                    open={pickerOpen.endPickerOpen}
                                    onClose={() => {
                                      setPickerOpen(prev => ({
                                        ...prev,
                                        endPickerOpen: false,
                                        endTextField: prev.isEndAccepted
                                          ? true
                                          : false,
                                      }));
                                    }}
                                    onClick={() =>
                                      setPickerOpen(prev => ({
                                        ...prev,
                                        endPickerOpen: true,
                                      }))
                                    }
                                    label="End Time"
                                    name={`${currentPath}.endTime`}
                                    placeholder="Select End Time"
                                    validate
                                    error={!!wsErrors?.endTime}
                                    helperText={wsErrors?.endTime?.message}
                                    requiredField
                                    value={
                                      pickerOpen.isEndAccepted
                                        ? storedEndTime
                                        : null
                                    }
                                    date={storedEndTime ?? null}
                                    onChange={(val: any) => {
                                      if (val?.toDate) {
                                        const ok = onUpdateTime(
                                          val.toDate(),
                                          'endTime',
                                          activeStep,
                                          true,
                                        );
                                        if (!ok) {
                                          setValue(
                                            `${currentPath}.endTime`,
                                            oldVal,
                                          );
                                          setReRenderKey(prev => prev + 1);
                                        } else {
                                          setReRenderKey(prev => prev + 1);
                                        }
                                      } else {
                                        setValue(
                                          `${currentPath}.endTime`,
                                          null,
                                        );
                                      }
                                      setPickerOpen(prev => ({
                                        ...prev,
                                        isEndAccepted: true,
                                      }));
                                    }}
                                  />
                                );
                              }

                              // When no variation, show TimePicker inline normally
                              return (
                                <TimePicker
                                  key={`end-time-${activeStep}-${reRenderKey}`}
                                  control={control}
                                  id={`end-time-${activeStep}`}
                                  disabled={disabled}
                                  minutesStep={TIME_PICKER_MINUTES_STEP}
                                  label="End Time"
                                  name={`${currentPath}.endTime`}
                                  placeholder="Select End Time"
                                  validate
                                  error={!!wsErrors?.endTime}
                                  helperText={wsErrors?.endTime?.message}
                                  requiredField
                                  value={storedEndTime ?? null}
                                  onChange={(val: any) => {
                                    if (val?.toDate) {
                                      const ok = onUpdateTime(
                                        val.toDate(),
                                        'endTime',
                                        activeStep,
                                        startVaries,
                                      );
                                      const oldVal = watch(
                                        `${currentPath}.endTime`,
                                      );
                                      if (!ok) {
                                        setValue(
                                          `${currentPath}.endTime`,
                                          oldVal,
                                        );
                                        setReRenderKey(prev => prev + 1);
                                      } else {
                                        setReRenderKey(prev => prev + 1);
                                      }
                                    } else {
                                      field.onChange(val);
                                    }
                                  }}
                                />
                              );
                            }}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  )}
                  {(getTimeDisplay(watch(`${currentPath}.shifts`) || [], false)
                    .startVaries ||
                    getTimeDisplay(watch(`${currentPath}.shifts`) || [], false)
                      .endVaries) && (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Various times selected. Editing the time will overwrite
                      the time.
                    </Typography>
                  )}
                </div>

                {/* --------- Extend Service (per worksheet) --------- */}
                {/* <div className="mb-24 btn-grp col-8"> */}
                {extendFields.length === 0 && (
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={() => ensureFirstExtendRow(activeStep)}
                    // onClick={handleExtendService}
                    className="lg-height sm-width mb-16"
                  >
                    Extend Service
                  </Button>
                )}
                {/* Extend Service Button */}
                {extendFields.length > 0 && (
                  <div className="d-flex align-center flex-between">
                    <div>
                      <p className="MuiTypography-body2 fw600">
                        Extend Service
                      </p>
                    </div>

                    <div>
                      <Button
                        variant="text"
                        size="medium"
                        onClick={() => {
                          // clear all extend rows for current worksheet
                          setValue(`${currentPath}.extendShifts`, [], {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      >
                        <u>Cancel</u>
                      </Button>
                    </div>
                  </div>
                )}

                {/* <div className="mb-24 btn-grp col-8">
                  <div>
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() => ensureFirstExtendRow(activeStep)}
                      className="lg-height"
                    >
                      Extend Service
                    </Button>
                  </div>

                  {extendFields.length > 0 && (
                  <div className="d-flex align-center flex-between">
                    <div>
                      <p className="MuiTypography-body2 fw600">
                        Extend Service
                      </p>
                    </div>

                    <div>
                      <Button
                        variant="text"
                        size="medium"
                        onClick={handleBackToEdit}
                      >
                        <u>Cancel</u>
                      </Button>
                    </div>
                  </div>
                )} */}

                {/* {extendFields.length > 0 && (
                    <div>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => {
                          // clear all extend rows for current worksheet
                          setValue(`${currentPath}.extendShifts`, [], {
                            shouldDirty: true,
                          });
                        }}
                        className="lg-height"
                      >
                        Cancel
                      </Button>
                    </div>
                  )} */}
                {/* </div> */}

                {extendFields.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    {extendFields.map((field: any, idx: number) => (
                      <Grid
                        container
                        spacing={1.5}
                        key={`extend-${activeStep}-${idx}-${reRenderKey}`}
                      >
                        <Grid item lg={5.5}>
                          <div className="form-in lg">
                            <DateInput
                              value={(() => {
                                const rowStart = watch(
                                  `${currentPath}.extendShifts.${idx}.startDate`,
                                );
                                if (rowStart) return rowStart;
                                if (idx > 0) {
                                  const prevEnd = watch(
                                    `${currentPath}.extendShifts.${
                                      idx - 1
                                    }.endDate`,
                                  );
                                  if (prevEnd != null) {
                                    const d = dayjs(prevEnd);
                                    if (d.isValid()) return d.add(1, 'day');
                                  }
                                }
                                const latestLastShift = product?.products?.find(
                                  (val: any) =>
                                    val?.cartId ===
                                    watch(`${currentPath}.worksheet`)?.cartId,
                                )?.endTime;
                                let base = dayjs(latestLastShift);
                                if (!base.isValid()) {
                                  base = dayjs(cartDetails?.endTime);
                                }
                                return base.isValid()
                                  ? base.add(1, 'day')
                                  : dayjs();
                              })()}
                              format={'MMM DD, YYYY'}
                              formatDate
                              requiredField
                              disabled={false}
                              label="Select Start Date"
                              placeholder="Start Date"
                              name={`${currentPath}.extendShifts.${idx}.startDate`}
                              formProps={{ control }}
                              validate
                              error={!!wsErrors?.extendShifts?.[idx]?.startDate}
                              helperText={
                                !!wsErrors?.extendShifts?.[idx]?.startDate && (
                                  <p className="helper-error">
                                    {
                                      wsErrors?.extendShifts?.[idx]?.startDate
                                        ?.message
                                    }
                                  </p>
                                )
                              }
                              onChange={(val: Dayjs | null) => {
                                if (!val) return;

                                const extendFields =
                                  watch(`${currentPath}.extendShifts`) || [];

                                const prevValue = watch(
                                  `${currentPath}.extendShifts.${idx}.startDate`,
                                );

                                const isValid = validateExtendDate(
                                  val,
                                  'startDate',
                                  idx,
                                  setValue,
                                  extendFields,
                                );

                                if (isValid) {
                                  setValue(
                                    `${currentPath}.extendShifts.${idx}.startDate`,
                                    val,
                                    { shouldDirty: true },
                                  );
                                } else {
                                  setValue(
                                    `${currentPath}.extendShifts.${idx}.startDate`,
                                    prevValue,
                                    {
                                      shouldDirty: true,
                                    },
                                  );
                                  setReRenderKey(prev => prev + 1);
                                }
                              }}
                            />
                          </div>
                        </Grid>
                        <Grid item lg={5.5}>
                          <div className="form-in lg">
                            <div className="form-in lg">
                              <DateInput
                                minDate={
                                  watch(
                                    `${currentPath}.extendShifts.${idx}.startDate`,
                                  ) || null
                                }
                                requiredField
                                disabled={false}
                                label="Select End Date"
                                placeholder="End Date"
                                name={`${currentPath}.extendShifts.${idx}.endDate`}
                                formProps={{ control }}
                                format={'MMM DD, YYYY'}
                                formatDate
                                validate
                                value={
                                  watch(
                                    `${currentPath}.extendShifts.${idx}.endDate`,
                                  )
                                    ? watch(
                                        `${currentPath}.extendShifts.${idx}.endDate`,
                                      )
                                    : null
                                }
                                error={!!wsErrors?.extendShifts?.[idx]?.endDate}
                                helperText={
                                  !!wsErrors?.extendShifts?.[idx]?.endDate && (
                                    <p className="helper-error">
                                      {
                                        wsErrors?.extendShifts?.[idx]?.endDate
                                          ?.message
                                      }
                                    </p>
                                  )
                                }
                                onChange={(val: Dayjs | null) => {
                                  if (!val) return;

                                  const extendFields =
                                    watch(`${currentPath}.extendShifts`) || [];

                                  const prevValue =
                                    watch(
                                      `${currentPath}.extendShifts.${idx}.endDate`,
                                    ) || null;

                                  const isValid = validateExtendDate(
                                    val,
                                    'endDate',
                                    idx,
                                    setValue,
                                    extendFields,
                                  );

                                  if (isValid) {
                                    setValue(
                                      `${currentPath}.extendShifts.${idx}.endDate`,
                                      val,
                                      {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                      },
                                    );
                                  } else {
                                    setValue(
                                      `${currentPath}.extendShifts.${idx}.endDate`,
                                      prevValue,
                                      {
                                        shouldDirty: true,
                                      },
                                    );
                                    setReRenderKey(prev => prev + 1);
                                  }
                                }}
                              />
                            </div>
                          </div>
                        </Grid>
                        <Grid item lg={1}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const currentExtendShifts =
                                watch(`${currentPath}.extendShifts`) || [];
                              const updatedExtendShifts =
                                currentExtendShifts.filter(
                                  (_: any, index: number) => index !== idx,
                                );
                              setValue(
                                `${currentPath}.extendShifts`,
                                updatedExtendShifts,
                                { shouldDirty: true, shouldValidate: true },
                              );
                            }}
                            sx={{ color: 'error.main' }}
                            className="mt44"
                          >
                            <i className="delete-icon"></i>
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}

                    <div className="text-right">
                      <Button
                        className="mt-12"
                        variant="text"
                        onClick={() => {
                          const currentExtendShifts =
                            watch(`${currentPath}.extendShifts`) || [];
                          const lastEndDate =
                            currentExtendShifts.at(-1)?.endDate;
                          if (!lastEndDate) {
                            notify(
                              'Please set end date for all extension periods before adding new ones.',
                              'error',
                            );
                            return;
                          }
                          const newExtendShift = {
                            startDate: lastEndDate
                              ? dayjs(lastEndDate).add(1, 'day')
                              : dayjs(cartDetails?.endTime).add(1, 'day'),
                            endDate: undefined,
                          };
                          setValue(
                            `${currentPath}.extendShifts`,
                            [...currentExtendShifts, newExtendShift],
                            { shouldDirty: true, shouldValidate: true },
                          );
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        <u>+ Add Range</u>
                      </Button>
                    </div>
                  </div>
                )}

                {/* --------- Notes (per worksheet) --------- */}
                <div className="form-in">
                  <label className="mb-8 d-block">Notes</label>
                  <TextField
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="Add Notes here..."
                    variant="outlined"
                    error={!!wsErrors?.notes}
                    helperText={wsErrors?.notes?.message}
                    value={notesState[activeStep] || ''}
                    onChange={e => {
                      const rawValue = e.target.value;

                      // Enforce 1000 char limit
                      const limitedValue =
                        rawValue.length > 1000
                          ? rawValue.substring(0, 1000)
                          : rawValue;

                      // Update local state (allow spaces while typing)
                      setNotesState(prev => ({
                        ...prev,
                        [activeStep]: limitedValue,
                      }));

                      //  Save trimmed value
                      setValue(`${currentPath}.notes`, limitedValue, {
                        shouldDirty: true,
                      });
                    }}
                    key={`notes-${activeStep}`} // Force re-render for each step
                  />
                </div>
                {/* --------- Notes (per worksheet) --------- */}

                <p className="text-right mb-12 mt-8">
                  {notesState[activeStep]?.length ?? 0}/1000
                </p>

                {/* --------- Stepper shortcuts (click to jump) --------- */}
                {/* <div className="mt-16">
                  <Grid container spacing={1}>
                    {worksheetFields.map((w, i) => (
                      <Grid item key={w.id}>
                        <Button
                          size="small"
                          variant={i === activeStep ? 'contained' : 'outlined'}
                          onClick={() => handleStepClick(i)}
                          sx={{ textTransform: 'none' }}
                        >
                          {i + 1}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </div> */}
              </div>
            </div>

            {/* --------- Actions --------- */}
            <div className="modal-actions">
              {activeStep <= 0 && (
                <div>
                  <Button
                    onClick={() => setWarningOpen(true)}
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    size="medium"
                    className="min_width_btn_160"
                  >
                    Cancel
                  </Button>
                </div>
              )}
              <div className="d-flex" style={{ gap: 12 }}>
                {activeStep > 0 && (
                  <Button
                    onClick={handleBack}
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    sx={{ textTransform: 'none' }}
                    size="medium"
                    className="min_width_btn_160"
                  >
                    Back
                  </Button>
                )}
                {/* <Button
                  type={
                    activeStep < worksheetFields.length - 1
                      ? 'button'
                      : 'submit'
                  }
                  onClick={
                    activeStep < worksheetFields.length - 1
                      ? handleNext
                      : undefined
                  }
                  variant="contained"
                  endIcon={
                    activeStep < worksheetFields.length - 1 ? (
                      <ArrowForwardIcon />
                    ) : undefined
                  }
                  size="medium"
                  className="min_width_btn_160"
                  sx={{ textTransform: 'none' }}
                >
                  {activeStep < worksheetFields.length - 1
                    ? 'Save & Continue'
                    : 'Save'}
                </Button> */}

                {activeStep < worksheetFields.length - 1 ? (
                  <Button
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      saveCurrentStepData();
                      handleNext();
                    }}
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    size="medium"
                    className="min_width_btn_160"
                    sx={{ textTransform: 'none' }}
                    disabled={isSubmitting || !isValid}
                  >
                    Save & Continue
                  </Button>
                ) : (
                  // ✅ Save Button (for last step)
                  <Button
                    type="button"
                    disabled={isSubmitting || !isValid}
                    onClick={e => {
                      e.preventDefault();
                      handleSubmit(onSubmit as any)();
                    }}
                    variant="contained"
                    size="medium"
                    className="min_width_btn_160"
                    sx={{ textTransform: 'none' }}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Location modal (per current worksheet) */}
      <LocationUpdateModalShift
        isOpen={openLocationUpdateModal}
        onClose={() => setOpenLocationUpdateModal(false)}
        onUpdate={loc => {
          // update in form (override for payload only) and reflect in UI title
          setValue(`${currentPath}.locationOverride`, loc, {
            shouldDirty: true,
          });
          const worksheet = watch(`${currentPath}.worksheet`) as any;
          setSelectedWorkSheet({
            ...worksheet,
            locationName: loc?.label || worksheet.locationName,
          });
        }}
        locationId={
          watch(`${currentPath}.locationOverride`)?.value ||
          (watch(`${currentPath}.worksheet.locationId`) as string)
        }
        id={(watch(`${currentPath}.worksheet.productionId`) as string) ?? ''}
        productionName={
          (watch(`${currentPath}.worksheet.productionName`) as string) ?? ''
        }
        currentLocation={
          (watch(`${currentPath}.locationOverride`)?.label as string) ??
          (watch(`${currentPath}.worksheet.locationName`) as string)
        }
        setId={(watch(`${currentPath}.worksheet.setId`) as string) ?? ''}
        _setName={(watch(`${currentPath}.worksheet.setName`) as string) ?? ''}
        branchId={productDetails?.products?.[0]?.branchId}
      />
      <WarningDialog
        open={warningOpen}
        title="Discard Changes"
        description="Cancelling will discard your edits. Do you want to continue?"
        type="warning"
        handleClose={() => setWarningOpen(false)}
        onClick={handleClose}
        cancelBtnText="Cancel"
        confirmBtnText="Yes, I'm sure"
      />
    </Dialog>
  );
};

export default EditShiftModal;
